// covered-by: frontends/carrie/src/components/ask-puntt-sources/__tests__/SourceHoverCard.test.tsx
import { Avatar } from "@radix-ui/themes";
import { type ComponentPropsWithoutRef, forwardRef } from "react";
import { useTranslation } from "react-i18next";

import { type AskPunttSource } from "./types";

const AVATAR_TONE_CLASS: Record<
  NonNullable<AskPunttSource["avatarTone"]>,
  string
> = {
  accent: "bg-puntt-accent-9 text-base-white",
  warning: "bg-puntt-warning-9 text-puntt-neutral-gray-12",
};

export function SourceAvatar({
  source,
  testIdPrefix,
  className,
}: {
  source: AskPunttSource;
  testIdPrefix: string;
  className?: string;
}) {
  const tone = source.avatarTone ?? "accent";
  const initials =
    source.initials != null && source.initials !== ""
      ? source.initials
      : source.title.slice(0, 2).toUpperCase();

  // why: initials sit next to a visible title — decorative for screen readers
  return (
    <span
      role="presentation"
      data-testid={`${testIdPrefix}-source-avatar-${source.id}`}
      className={className}
    >
      <Avatar
        size="1"
        radius="full"
        fallback={initials}
        className={AVATAR_TONE_CLASS[tone]}
      />
    </span>
  );
}

export type SourceCountClusterProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "children"
> & {
  sources: AskPunttSource[];
  testIdPrefix: string;
  /** Cap how many stacked avatars render (Figma shows two). */
  maxAvatars?: number;
  /** When true, render a non-interactive cluster (multi-source hover header). */
  displayOnly?: boolean;
};

// why forwardRef + rest props: the "count" trigger of SourceHoverCard renders
// this inside HoverCard.Trigger asChild; the Slot's ref and pointer handlers
// must reach the button or the card never opens on hover.
export const SourceCountCluster = forwardRef<
  HTMLButtonElement,
  SourceCountClusterProps
>(function SourceCountCluster(
  {
    sources,
    testIdPrefix,
    maxAvatars = 2,
    displayOnly = false,
    className,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation("ui");
  const count = sources.length;
  const visible = sources.slice(0, maxAvatars);

  if (count === 0) return null;

  const body = (
    <>
      <span className="flex items-center" aria-hidden="true">
        {visible.map((source, index) => (
          <SourceAvatar
            key={source.id}
            source={source}
            testIdPrefix={testIdPrefix}
            className={index > 0 ? "-ml-1.5" : undefined}
          />
        ))}
      </span>
      <span className="whitespace-nowrap text-[12px] leading-4 tracking-[0.04px] text-puntt-neutral-gray-11">
        {t("askPuntt.sources.count", { count })}
      </span>
    </>
  );

  if (displayOnly) {
    return (
      <span
        data-testid={`${testIdPrefix}-source-count`}
        className={["inline-flex items-center gap-2", className]
          .filter(Boolean)
          .join(" ")}
      >
        {body}
      </span>
    );
  }

  return (
    <button
      {...rest}
      ref={ref}
      type="button"
      data-testid={`${testIdPrefix}-source-count`}
      aria-label={t("askPuntt.sources.countAria", { count })}
      className={[
        "inline-flex cursor-pointer items-center gap-2 bg-transparent p-0 text-left",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {body}
    </button>
  );
});
